// "New group": a name and the people, then Create group.
//
// The app's "New group" screen (apps/mobile/src/screens/social/DmGroupCreateScreen.tsx),
// with the same routes and the same body. The people offered come from the group
// picker route, which lists only people the server would let this person add (their
// role has Group Chats, they are in the project, nobody who has blocked them). The
// server checks each one again when the group is made and refuses the lot if one of
// them cannot be added; then nothing was made, and the person can take them out and
// try again.
//
// NEVER TWO GROUPS FOR ONE. The client id comes from the page and goes with every try,
// so a double click or a try after a lost answer gets the group the first try made,
// never a second one. The page keeps the same id for the next opening in this project
// until a group has been made with it: a person who gave up after a lost answer and
// starts again gets that group, not a copy. While a try is on its way the pop-up does
// not close (Escape, the page behind and the close button wait for the answer).
//
// People picked outlive the search: someone picked from one search stays picked when
// the box is cleared or other words are typed, and is shown as a chip with a way out.
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { InlineLoader } from '../loading';
import { GROUP_LIMITS, messagesAPI, type PickPerson } from '../../services/messagesApi';
import MessagesDialog from './MessagesDialog';
import { FindFailureLine, FindField, ListHeading, PickedChips, Quiet, TickRow } from './PickParts';
import { usePeopleFinder } from './usePeopleFinder';

interface NewGroupDialogProps {
  projectId: number;
  /** The id that makes a retry safe (newClientMsgId). The page keeps it until a group is made with it. */
  clientGroupId: string;
  onClose: () => void;
  /** The group is made: the page opens it. */
  onCreated: (groupId: string, title: string) => void;
}

/** People who can be picked: a group holds MAX_PEOPLE, the person making it included. */
const ROOM = GROUP_LIMITS.MAX_PEOPLE - 1;
/** Fewest people to pick: MIN_PEOPLE, the person making it included. */
const FEWEST = GROUP_LIMITS.MIN_PEOPLE - 1;

/** A group name as the server keeps it: one space between words, none at the ends. */
export const cleanTitle = (raw: string): string => raw.replace(/\s+/g, ' ').trim();

/** "A", "A and B", "A, B and C", "A, B and 3 others". */
export const namesLine = (names: string[]): string => {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} others`;
};

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;
const bodyOf = (err: unknown): { code?: unknown; message?: unknown; notAllowed?: unknown } | undefined =>
  (err as { response?: { data?: { code?: unknown; message?: unknown; notAllowed?: unknown } } } | null)?.response?.data;

/**
 * The server's "some of these people cannot be added" (code members_not_allowed), in
 * one line that names them, so they can be taken out. The reason is never given, and
 * the server never says it. Null for any other answer.
 */
export const notAllowedLine = (err: unknown, picked: PickPerson[]): string | null => {
  const said = bodyOf(err);
  if (said?.code !== 'members_not_allowed') return null;
  const codes = Array.isArray(said.notAllowed) ? said.notAllowed.filter((c): c is string => typeof c === 'string') : [];
  const byCode = new Map(picked.map((p) => [p.empCode.toLowerCase(), p.name]));
  const names = codes.map((c) => byCode.get(c.toLowerCase())).filter((n): n is string => !!n);
  return names.length > 0 && names.length === codes.length
    ? `${namesLine(names)} cannot be added.`
    : 'Some of these people cannot be added.';
};

/** Why the group was not made, in one line. */
export const createRefusal = (err: unknown, picked: PickPerson[]): string => {
  const status = statusOf(err);
  const said = bodyOf(err);
  // Nothing was made.
  const named = notAllowedLine(err, picked);
  if (named) return named;
  if (status === 404 || status === 403) return 'Creating groups is not available here.';
  if (status === 401) return 'Your sign-in could not be confirmed.';
  // The server's own words for a request it would not take: "Add at least one other person".
  if (status === 400 && typeof said?.message === 'string' && said.message.trim()) return said.message;
  return 'The group could not be created. Try again.';
};

export default function NewGroupDialog({ projectId, clientGroupId, onClose, onCreated }: NewGroupDialogProps): ReactElement {
  const [title, setTitle] = useState('');
  const [typed, setTyped] = useState('');
  const [picked, setPicked] = useState<PickPerson[]>([]);
  const [creating, setCreating] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const nameField = useRef<HTMLInputElement>(null);
  const creatingNow = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const { people, loading, failure, searching, retry } = usePeopleFinder(projectId, typed, messagesAPI.groupPeople);

  const name = cleanTitle(title);
  const full = picked.length >= ROOM;
  const ready = name.length > 0 && picked.length >= FEWEST && !creating;
  const isPicked = (person: PickPerson) => picked.some((p) => p.empCode === person.empCode);

  const toggle = (person: PickPerson) => {
    if (creating) return;
    setRefusal(null);
    setPicked((was) => {
      if (was.some((p) => p.empCode === person.empCode)) return was.filter((p) => p.empCode !== person.empCode);
      return was.length >= ROOM ? was : [...was, person];
    });
  };

  const create = async () => {
    // The ref stops a second click that lands before the first has redrawn the button.
    if (!ready || creatingNow.current) return;
    creatingNow.current = true;
    setCreating(true);
    setRefusal(null);
    try {
      const res = await messagesAPI.createGroup(projectId, name, picked.map((p) => p.empCode), clientGroupId);
      const made = res?.data?.data;
      if (!alive.current) return;
      if (made?.groupId) onCreated(String(made.groupId), made.title || name);
      else setRefusal('The group could not be created. Try again.');
    } catch (err) {
      if (alive.current) setRefusal(createRefusal(err, picked));
    } finally {
      creatingNow.current = false;
      if (alive.current) setCreating(false);
    }
  };

  // With nothing searched, people picked from an earlier search come first, so they can
  // always be seen and taken out. A search shows its own results only.
  const listed = new Set(people.map((p) => p.empCode));
  const rows = searching ? people : [...picked.filter((p) => !listed.has(p.empCode)), ...people];

  let body: ReactElement;
  if (failure) {
    body = <FindFailureLine failure={failure} off="Creating groups is not available here." onRetry={retry} />;
  } else if (rows.length === 0) {
    body = loading
      ? <InlineLoader label="Loading people…" className="flex justify-center py-6" />
      : <Quiet>{searching ? 'No one by that name.' : 'Type a name to find someone.'}</Quiet>;
  } else {
    body = (
      <>
        <ListHeading searching={searching} />
        <ul aria-label="People" className="space-y-0.5">
          {rows.map((person) => {
            const on = isPicked(person);
            return (
              <li key={person.empCode}>
                <TickRow
                  person={person}
                  on={on}
                  onToggle={() => toggle(person)}
                  // No room left: the people already picked can still be taken out.
                  disabled={creating || (!on && full)}
                  title={!on && full ? `A group holds ${GROUP_LIMITS.MAX_PEOPLE} people.` : undefined}
                />
              </li>
            );
          })}
        </ul>
      </>
    );
  }

  return (
    <MessagesDialog title="New group" onClose={onClose} initialFocus={nameField} busy={creating}>
      <div className="flex-none space-y-2 px-5 pb-3">
        <div className="relative">
          <input
            ref={nameField}
            type="text"
            aria-label="Group name"
            placeholder="Group name"
            autoComplete="off"
            maxLength={GROUP_LIMITS.TITLE_CHARS}
            value={title}
            disabled={creating}
            onChange={(e) => {
              setTitle(e.target.value.slice(0, GROUP_LIMITS.TITLE_CHARS));
              setRefusal(null);
            }}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-14 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:bg-gray-50"
          />
          {/* Said only near the limit, where it is useful. */}
          {title.length >= GROUP_LIMITS.TITLE_CHARS - 10 && (
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              {title.length}/{GROUP_LIMITS.TITLE_CHARS}
            </span>
          )}
        </div>
        <FindField value={typed} onChange={setTyped} busy={loading && rows.length > 0} disabled={creating} />
        <PickedChips picked={picked} disabled={creating} onUnpick={toggle} />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">{body}</div>
      <div className="flex-none border-t border-gray-100 px-5 py-3">
        {refusal && <p role="alert" className="mb-2 text-sm text-red-600">{refusal}</p>}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => { void create(); }}
            disabled={!ready}
            aria-label="Create group"
            title={ready || creating ? undefined : 'Name the group and pick at least one person.'}
            className="flex h-10 items-center rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create group'}
          </button>
        </div>
      </div>
    </MessagesDialog>
  );
}
